import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import { SelectionProvider } from "./context/SelectionContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { PermissionGuard } from "./auth/PermissionGuard";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { ToastContext, useToastState } from "./hooks/useToast";
import { Spinner } from "./components/ui/Spinner";

// Lazy-loaded pages
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const CreateAccountPage = lazy(() => import("./pages/CreateAccountPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));

// Users
const UsersListPage = lazy(() => import("./pages/users/UsersListPage"));
const UserDetailPage = lazy(() => import("./pages/users/UserDetailPage"));

// Self profile
const ProfilePage = lazy(() => import("./pages/ProfilePage"));

// Roles
const RolesListPage = lazy(() => import("./pages/roles/RolesListPage"));
const RoleCreatePage = lazy(() => import("./pages/roles/RoleCreatePage"));
const RoleDetailPage = lazy(() => import("./pages/roles/RoleDetailPage"));
const PermissionsPage = lazy(() => import("./pages/roles/PermissionsPage"));

// Entities
const EntitiesListPage = lazy(() => import("./pages/entities/EntitiesListPage"));
const EntityCreatePage = lazy(() => import("./pages/entities/EntityCreatePage"));
const EntityDetailPage = lazy(() => import("./pages/entities/EntityDetailPage"));

// Organizations
const OrgsListPage = lazy(() => import("./pages/orgs/OrgsListPage"));
const OrgCreatePage = lazy(() => import("./pages/orgs/OrgCreatePage"));
const OrgDetailPage = lazy(() => import("./pages/orgs/OrgDetailPage"));

// Customers
const CustomersListPage = lazy(() => import("./pages/customers/CustomersListPage"));
const CustomerDetailPage = lazy(() => import("./pages/customers/CustomerDetailPage"));

// Addresses
const AddressesPage = lazy(() => import("./pages/addresses/AddressesPage"));
const AddressTypesPage = lazy(() => import("./pages/addresses/AddressTypesPage"));

// Operations
const OperationsListPage = lazy(() => import("./pages/operations/OperationsListPage"));
const OperationCreatePage = lazy(() => import("./pages/operations/OperationCreatePage"));
const OperationDetailPage = lazy(() => import("./pages/operations/OperationDetailPage"));
const OperationTypesPage = lazy(() => import("./pages/operations/OperationTypesPage"));

// Products
const ProductsListPage = lazy(() => import("./pages/products/ProductsListPage"));
const ProductDetailPage = lazy(() => import("./pages/products/ProductDetailPage"));

// SKUs
const SkusListPage = lazy(() => import("./pages/skus/SkusListPage"));
const SkuDetailPage = lazy(() => import("./pages/skus/SkuDetailPage"));

// Labels
const LabelsListPage = lazy(() => import("./pages/labels/LabelsListPage"));
const LabelDetailPage = lazy(() => import("./pages/labels/LabelDetailPage"));

// Taxonomies
const TaxonomyTreePage = lazy(() => import("./pages/taxonomies/TaxonomyTreePage"));

// Product Mixes
const ProductMixesPage = lazy(() => import("./pages/productMixes/ProductMixesPage"));

// Manufacturers & Vendors
const ManufacturersListPage = lazy(() => import("./pages/manufacturers/ManufacturersListPage"));
const ManufacturerDetailPage = lazy(() => import("./pages/manufacturers/ManufacturerDetailPage"));
const VendorsListPage = lazy(() => import("./pages/vendors/VendorsListPage"));
const VendorDetailPage = lazy(() => import("./pages/vendors/VendorDetailPage"));
const EndCustomersListPage = lazy(() => import("./pages/endCustomers/EndCustomersListPage"));
const EndCustomerDetailPage = lazy(() => import("./pages/endCustomers/EndCustomerDetailPage"));

// Jobs & Tasks
const JobsListPage = lazy(() => import("./pages/jobs/JobsListPage"));
const JobCreatePage = lazy(() => import("./pages/jobs/JobCreatePage"));
const JobDetailPage = lazy(() => import("./pages/jobs/JobDetailPage"));
const JobSchedulingPickerPage = lazy(() => import("./pages/jobs/JobSchedulingPickerPage"));
const TaskDetailPage = lazy(() => import("./pages/tasks/TaskDetailPage"));

// Notifications
const NotificationsPage = lazy(() => import("./pages/notifications/NotificationsPage"));
const NotificationDetailPage = lazy(() => import("./pages/notifications/NotificationDetailPage"));

// User Labels
const UserLabelsListPage = lazy(() => import("./pages/userLabels/UserLabelsListPage"));

// User Associations
const UserAssociationsPage = lazy(() => import("./pages/users/UserAssociationsPage"));

// Orders
const OrdersListPage = lazy(() => import("./pages/orders/OrdersListPage"));
const OrderCreatePage = lazy(() => import("./pages/orders/OrderCreatePage"));
const OrderDetailPage = lazy(() => import("./pages/orders/OrderDetailPage"));

// Scheduling
const SchedulingListPage = lazy(() => import("./pages/scheduling/SchedulingListPage"));
const SchedulingCreatePage = lazy(() => import("./pages/scheduling/SchedulingCreatePage"));
const SchedulingDetailPage = lazy(() => import("./pages/scheduling/SchedulingDetailPage"));

// Deliverymen & Carriers
const DeliverymenListPage = lazy(() => import("./pages/deliverymen/DeliverymenListPage"));
const CarriersListPage = lazy(() => import("./pages/carriers/CarriersListPage"));

// Stock
const ProjectedStockPage = lazy(() => import("./pages/stock/ProjectedStockPage"));

// Vehicles
const VehiclesListPage = lazy(() => import("./pages/vehicles/VehiclesListPage"));
const VehicleDetailPage = lazy(() => import("./pages/vehicles/VehicleDetailPage"));

// Admin
const AdminPage = lazy(() => import("./pages/admin/AdminPage"));

// Log Addresses
const LogAddressesPage = lazy(() => import("./pages/logAddresses/LogAddressesPage"));
const LogAddressSettingsPage = lazy(() => import("./pages/logAddresses/LogAddressSettingsPage"));

// Units
const UnitGroupsPage = lazy(() => import("./pages/units/UnitGroupsPage"));

// Ledger
const LedgerLabelsPage = lazy(() => import("./pages/ledger/LedgerLabelsPage"));

function Loading() {
  return (
    <div className="flex items-center justify-center py-24">
      <Spinner size="lg" />
    </div>
  );
}

export function App() {
  const toastState = useToastState();

  return (
    <ToastContext.Provider value={toastState}>
      <AuthProvider>
        <SelectionProvider>
        <BrowserRouter>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/create" element={<CreateAccountPage />} />

              <Route element={<ProtectedRoute />}>
                <Route element={<PermissionGuard />}>
                <Route element={<DashboardLayout />}>
                  <Route index element={<DashboardPage />} />

                  {/* Self profile (no permission gate via routePermissions) */}
                  <Route path="profile" element={<ProfilePage />} />

                  {/* Users */}
                  <Route path="users" element={<UsersListPage />} />
                  <Route path="users/:email" element={<UserDetailPage />} />

                  {/* Roles & Permissions */}
                  <Route path="roles" element={<RolesListPage />} />
                  <Route path="roles/create" element={<RoleCreatePage />} />
                  <Route path="roles/permissions" element={<PermissionsPage />} />
                  <Route path="roles/:id" element={<RoleDetailPage />} />

                  {/* Entities */}
                  <Route path="entities" element={<EntitiesListPage />} />
                  <Route path="entities/create" element={<EntityCreatePage />} />
                  <Route path="entities/:id" element={<EntityDetailPage />} />

                  {/* Organizations */}
                  <Route path="orgs" element={<OrgsListPage />} />
                  <Route path="orgs/create" element={<OrgCreatePage />} />
                  <Route path="orgs/:id" element={<OrgDetailPage />} />

                  {/* Customers */}
                  <Route path="customers" element={<CustomersListPage />} />
                  <Route path="customers/:id" element={<CustomerDetailPage />} />

                  {/* Addresses */}
                  <Route path="addresses" element={<AddressesPage />} />
                  <Route path="address-types" element={<AddressTypesPage />} />

                  {/* Operations */}
                  <Route path="operations" element={<OperationsListPage />} />
                  <Route path="operations/create" element={<OperationCreatePage />} />
                  <Route path="operations/:id" element={<OperationDetailPage />} />
                  <Route path="operation-types" element={<OperationTypesPage />} />

                  {/* Products */}
                  <Route path="products" element={<ProductsListPage />} />
                  <Route path="products/:id" element={<ProductDetailPage />} />

                  {/* SKUs */}
                  <Route path="skus" element={<SkusListPage />} />
                  <Route path="skus/:id" element={<SkuDetailPage />} />

                  {/* Labels */}
                  <Route path="labels" element={<LabelsListPage />} />
                  <Route path="labels/:id" element={<LabelDetailPage />} />

                  {/* Taxonomies */}
                  <Route path="taxonomies" element={<TaxonomyTreePage />} />

                  {/* Product Mixes */}
                  <Route path="product-mixes" element={<ProductMixesPage />} />

                  {/* Manufacturers & Vendors */}
                  <Route path="manufacturers" element={<ManufacturersListPage />} />
                  <Route path="manufacturers/:id" element={<ManufacturerDetailPage />} />
                  <Route path="vendors" element={<VendorsListPage />} />
                  <Route path="vendors/:id" element={<VendorDetailPage />} />
                  <Route path="end-customers" element={<EndCustomersListPage />} />
                  <Route path="end-customers/:id" element={<EndCustomerDetailPage />} />

                  {/* Jobs & Tasks */}
                  <Route path="jobs" element={<JobsListPage />} />
                  <Route path="jobs/create" element={<JobCreatePage />} />
                  <Route path="jobs/pick-io/:direction" element={<JobSchedulingPickerPage />} />
                  <Route path="jobs/:id" element={<JobDetailPage />} />
                  <Route path="tasks/:id" element={<TaskDetailPage />} />

                  {/* Notifications */}
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="notifications/:id" element={<NotificationDetailPage />} />

                  {/* User Labels */}
                  <Route path="user-labels" element={<UserLabelsListPage />} />

                  {/* User Associations */}
                  <Route path="user-associations" element={<UserAssociationsPage />} />

                  {/* Orders */}
                  <Route path="orders" element={<OrdersListPage />} />
                  <Route path="orders/create" element={<OrderCreatePage />} />
                  <Route path="orders/:id" element={<OrderDetailPage />} />

                  {/* Scheduling */}
                  <Route path="scheduling" element={<SchedulingListPage />} />
                  <Route path="scheduling/create" element={<SchedulingCreatePage />} />
                  <Route path="scheduling/:id" element={<SchedulingDetailPage />} />

                  {/* Deliverymen & Carriers */}
                  <Route path="deliverymen" element={<DeliverymenListPage />} />
                  <Route path="carriers" element={<CarriersListPage />} />

                  {/* Stock */}
                  <Route path="projected-stock" element={<ProjectedStockPage />} />

                  {/* Vehicles */}
                  <Route path="vehicles" element={<VehiclesListPage />} />
                  <Route path="vehicles/:id" element={<VehicleDetailPage />} />

                  {/* Log Addresses */}
                  <Route path="log-addresses" element={<LogAddressesPage />} />
                  <Route path="log-addresses/settings" element={<LogAddressSettingsPage />} />

                  {/* Units */}
                  <Route path="unit-groups" element={<UnitGroupsPage />} />

                  {/* Admin */}
                  <Route path="admin" element={<AdminPage />} />

                  {/* Ledger */}
                  <Route path="labels/:labelId/ledger" element={<LedgerLabelsPage />} />
                </Route>
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        </SelectionProvider>
      </AuthProvider>
    </ToastContext.Provider>
  );
}
